/**
 * <XtsStations> — Renders the configured StationConfig[].
 *
 * Per station:
 *   1) Build a polyline from start..end position along the part's path.
 *   2) Render a PathTube colored with the station color.
 *   3) Render markers at stopPositions[] — geometry chosen per-station via
 *      `StationConfig.stopMarkerShape` (falls back to the global
 *      `display.stationMarkerOptions.shape`).
 *   4) Optionally render the station description as a camera-facing
 *      billboard at the centre of the tube
 *      (`StationOptions.showStationDescription`).
 *   5) Optionally render the mm-value of each stop position next to its
 *      marker (`StationOptions.showStopPositionValues`).
 *
 * Lateral / vertical offsets (`StationOptions.lateralDisplacementMm` +
 * `displacementMm`) move the entire overlay (tube + markers + labels)
 * sideways and up/down. Mirrors the Dimensions overlay convention: each
 * sample at centerline s is rendered perpendicular to that point, so
 * displayed values stay aligned with the actual track.
 *
 * Multi-part stations: spec says iterate partOids[] and accumulate. The
 * single-part loop closure (endPos < startPos) duplicates partOid.
 */

import React, { useMemo } from 'react';
import type { BuiltChain } from '../../geometry/ChainBuilder.js';
import {
  sampleChainPoint,
  sampleChainRange,
} from '../../geometry/sampleChain.js';
import {
  chainEndpointsForPart,
  resolveStopPositionMm,
  userToChainPositionMm,
} from '../../geometry/positionFrame.js';
import { PathTube } from './PathTube.js';
import { PathMarker } from './PathMarker.js';
import { DimensionLabel } from './DimensionLabel.js';
import { PartTransformGroup } from './PartTransformGroup.js';
import type {
  DisplayOptions,
  MarkerShape,
  PartTransformation,
  PositionFrame,
  StationConfig,
} from '../../types.js';

interface XtsStationsProps {
  stations: StationConfig[];
  chainsByPartOid: Map<number, BuiltChain>;
  /**
   * Per-part transforms keyed by `PartConfig.globalNumber`. Each station
   * is rendered inside the transform group of its FIRST partOid so it
   * follows the part as it moves (e.g. lift parts).
   */
  partTransformsByPartOid?: Map<number, PartTransformation | undefined>;
  display?: DisplayOptions;
  /** XPU position frame applied to start/end/stops. */
  positionFrame?: PositionFrame;
}

const XtsStationsImpl: React.FC<XtsStationsProps> = ({
  stations,
  chainsByPartOid,
  partTransformsByPartOid,
  display,
  positionFrame,
}) => {
  const stationOpts = display?.stationOptions;
  const thickness = stationOpts?.thicknessMm ?? 8;
  const liftZ = stationOpts?.displacementMm ?? 60;
  const lateralMm = stationOpts?.lateralDisplacementMm ?? 0;
  const defaultMarkerShape: MarkerShape =
    display?.stationMarkerOptions?.shape ?? 'Diamond';
  const defaultMarkerSize = display?.stationMarkerOptions?.sizeMm ?? 12;
  const showDescription = stationOpts?.showStationDescription ?? false;
  const showStopValues = stationOpts?.showStopPositionValues ?? false;
  const labelSize = stationOpts?.textOptions?.sizeMm ?? 30;
  const labelColorOverride = stationOpts?.labelColorOverride;
  const fontFamily = stationOpts?.textOptions?.fontFamily;

  return (
    <group name="xts-stations">
      {stations
        .filter((s) => s.isEnabled)
        .map((s) => {
          // Anchor multi-part stations to their FIRST partOid — chain
          // math is part-local, can't stitch across differently-
          // transformed parts.
          const anchorOid = s.partOids[0];
          const partTransform =
            anchorOid !== undefined
              ? partTransformsByPartOid?.get(anchorOid)
              : undefined;
          return (
            <PartTransformGroup
              key={s.stationId}
              name={`station-wrap-${s.stationId}`}
              partTransformation={partTransform}
            >
              <Station
                station={s}
                chainsByPartOid={chainsByPartOid}
                thickness={thickness}
                liftZ={liftZ}
                lateralMm={lateralMm}
                defaultMarkerShape={defaultMarkerShape}
                defaultMarkerSize={defaultMarkerSize}
                showDescription={showDescription}
                showStopValues={showStopValues}
                labelSize={labelSize}
                labelColorOverride={labelColorOverride}
                fontFamily={fontFamily}
                positionFrame={positionFrame}
              />
            </PartTransformGroup>
          );
        })}
    </group>
  );
};

export const XtsStations = React.memo(XtsStationsImpl);
XtsStations.displayName = 'XtsStations';

const Station: React.FC<{
  station: StationConfig;
  chainsByPartOid: Map<number, BuiltChain>;
  thickness: number;
  liftZ: number;
  lateralMm: number;
  defaultMarkerShape: MarkerShape;
  defaultMarkerSize: number;
  showDescription: boolean;
  showStopValues: boolean;
  labelSize: number;
  labelColorOverride?: string;
  fontFamily?: string;
  positionFrame?: PositionFrame;
}> = ({
  station,
  chainsByPartOid,
  thickness,
  liftZ,
  lateralMm,
  defaultMarkerShape,
  defaultMarkerSize,
  showDescription,
  showStopValues,
  labelSize,
  labelColorOverride,
  fontFamily,
  positionFrame,
}) => {
  const colorHex = useMemo(
    () => argbIntToHex(station.stationColor),
    [station.stationColor],
  );
  // Label colour: explicit override beats per-station tube colour.
  const labelColor = labelColorOverride ?? colorHex;
  const markerShape: MarkerShape =
    station.stopMarkerShape ?? defaultMarkerShape;
  const markerSize = station.stopMarkerSizeMm ?? defaultMarkerSize;

  // Build segments. For multi-part: each partOid contributes a polyline.
  // start/end values come from user-frame; transform once per render
  // through the XPU's positionFrame so the rest of the segment math
  // stays in chain-frame.
  const segments = useMemo(() => {
    const parts =
      station.partOids.length === 1 &&
      station.endPositionOnPart < station.startPositionOnPart
        ? [station.partOids[0]!, station.partOids[0]!]
        : station.partOids;

    const out: Array<{ chain: BuiltChain; start: number; end: number }> = [];
    for (let i = 0; i < parts.length; i++) {
      const chain = chainsByPartOid.get(parts[i]!);
      if (!chain) continue;
      const isFirst = i === 0;
      const isLast = i === parts.length - 1;
      // For non-first / non-last parts the segment spans the entire
      // chain. For direction='negative' the user-frame start is at
      // chain.trackLengthMm and the user-frame end at 0, so the segment
      // terminates on the correct end of the chain instead of overshooting
      // by one full part.
      const ep = chainEndpointsForPart(chain.trackLengthMm, positionFrame);
      const start = isFirst
        ? userToChainPositionMm(station.startPositionOnPart, positionFrame)
        : ep.userStartChainMm;
      const end = isLast
        ? userToChainPositionMm(station.endPositionOnPart, positionFrame)
        : ep.userEndChainMm;
      out.push({ chain, start, end });
    }
    return out;
  }, [station, chainsByPartOid, positionFrame]);

  // Description label sits at the midpoint of the (first) segment.
  const descriptionAnchor = useMemo(() => {
    if (!showDescription || segments.length === 0) return null;
    const seg = segments[0]!;
    const mid = (seg.start + seg.end) / 2;
    return sampleChainPoint(seg.chain, mid, liftZ, lateralMm);
  }, [showDescription, segments, liftZ, lateralMm]);

  return (
    <group name={`station-${station.stationId}`}>
      {segments.map((seg, i) => {
        const points = sampleChainRange(
          seg.chain,
          seg.start,
          seg.end,
          20,
          liftZ,
          lateralMm,
        );
        return (
          <PathTube
            key={i}
            points={points}
            thicknessMm={thickness}
            color={colorHex}
          />
        );
      })}

      {/* Stop markers + optional value labels.
       *  Stop values are interpreted via `resolveStopPositionMm` —
       *  station-relative or track-relative depending on
       *  `stopPositionsRelativeTo`, then through the XPU's
       *  positionFrame. The label still shows the raw user-frame value
       *  (so "300 mm" stays "300 mm" regardless of how the chain
       *  internally addresses that point). */}
      {(station.stopPositions ?? []).map((pos, i) => {
        const chain = chainsByPartOid.get(station.partOids[0]!);
        if (!chain) return null;
        const chainPos = resolveStopPositionMm(pos, station, positionFrame);
        // Sample at the SAME (lift, lateral) as the station tube so the
        // marker sits centred on the path. The previous +4 mm Z bias
        // left the marker hovering just above the tube.
        const sample = sampleChainPoint(
          chain,
          chainPos,
          liftZ,
          lateralMm,
        );
        if (!sample) return null;
        return (
          <React.Fragment key={i}>
            <PathMarker
              position={sample.position}
              tangent={sample.tangent}
              shape={markerShape}
              sizeMm={markerSize}
              color={colorHex}
            />
            {showStopValues && (
              <DimensionLabel
                positionMm={[
                  sample.position.x,
                  sample.position.y,
                  sample.position.z,
                ]}
                text={`${Math.round(pos)} mm`}
                sizeMm={labelSize * 0.85}
                color={labelColor}
                fontFamily={fontFamily}
                liftMm={markerSize * 0.6 + labelSize * 0.4}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Station description billboard at tube midpoint */}
      {descriptionAnchor && station.description && (
        <DimensionLabel
          positionMm={[
            descriptionAnchor.position.x,
            descriptionAnchor.position.y,
            descriptionAnchor.position.z,
          ]}
          text={station.description}
          sizeMm={labelSize}
          color={labelColor}
          fontFamily={fontFamily}
          liftMm={thickness * 1.5 + labelSize * 0.3}
        />
      )}
    </group>
  );
};

/**
 * 2D-canvas storage uses 32-bit ARGB ints. We strip alpha and emit
 * `#RRGGBB`. (Three.js MeshStandardMaterial.transparent handles opacity
 * separately so we don't need the alpha channel here.)
 */
export function argbIntToHex(argb: number): string {
  const r = (argb >> 16) & 0xff;
  const g = (argb >> 8) & 0xff;
  const b = argb & 0xff;
  const hex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
  return `#${hex}`;
}
